import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import StarField from '../home/components/StarField';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { certificates } from '../../mocks/certificates';
import type { Certificate } from '../../mocks/certificates';
import { useLang } from '../../i18n/LanguageContext';

function CertificateCard({ cert }: { cert: Certificate }) {
  const { t } = useLang();
  const gradeColor =
    cert.grade === 'Distinction' ? 'text-[#f4d03f]' :
    cert.grade === 'Merit' ? 'text-[#c8b6ff]' : 'text-white';

  const statusBadge =
    cert.status === 'valid' ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300' :
    cert.status === 'expired' ? 'bg-orange-500/20 border-orange-500/40 text-orange-300' :
    'bg-red-500/20 border-red-500/40 text-red-300';

  const statusLabel =
    cert.status === 'valid' ? t('Valid', '有效') :
    cert.status === 'expired' ? t('Expired', '已过期') : t('Revoked', '已撤销');

  return (
    <div className="relative bg-[#2d1b4e]/80 backdrop-blur-md rounded-3xl border border-[#d4af37]/30 overflow-hidden shadow-2xl shadow-[#d4af37]/10 animate-fade-in">
      <div className="h-2 w-full bg-gradient-to-r from-[#d4af37] via-[#f4d03f] to-[#d4af37]" />
      <div className="p-4 sm:p-10">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6 sm:mb-8">
          <div className="flex items-center space-x-3">
            <img src="https://static.readdy.ai/image/f4ea41724fdff553620ff2345610d616/46f66eaf14331da7e5a01089d8e059d5.png" alt="Logo" className="w-10 h-10 sm:w-14 sm:h-14 object-contain" />
            <div>
              <p className="text-[#d4af37] font-serif text-base sm:text-xl font-bold">SSD Mystic Academy</p>
              <p className="text-[#c8b6ff] text-xs">Saint Star Dimension · {t('Official Certificate', '官方证书')}</p>
            </div>
          </div>
          <span className={`px-3 py-1.5 rounded-full border text-xs sm:text-sm font-medium ${statusBadge}`}>{statusLabel}</span>
        </div>

        <div className="text-center mb-6 sm:mb-8">
          <p className="text-[#c8b6ff] text-xs sm:text-sm tracking-widest uppercase mb-2">{t('Certificate of Completion', '结业证书')}</p>
          <h2 className="text-2xl sm:text-4xl font-serif font-bold text-white mb-1">{t('This certifies that', '兹证明')}</h2>
        </div>

        <div className="flex flex-col items-center mb-6 sm:mb-8">
          <h3 className="text-2xl sm:text-3xl font-serif font-bold text-[#d4af37] mb-1">{cert.studentName}</h3>
          <p className="text-[#c8b6ff] text-sm">{t('has successfully completed the course', '已成功完成以下课程')}</p>
        </div>

        <div className="text-center bg-[#1a0b2e]/50 rounded-2xl py-4 sm:py-5 px-4 sm:px-8 mb-6 sm:mb-8 border border-[#d4af37]/20">
          <p className="text-lg sm:text-2xl font-bold text-white">{cert.courseName}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="bg-[#1a0b2e]/40 rounded-xl p-3 sm:p-4 border border-[#d4af37]/10 text-center">
            <p className="text-[#c8b6ff] text-xs mb-1">{t('Certificate ID', '证书编号')}</p>
            <p className="text-white font-mono font-bold text-xs sm:text-sm">{cert.id}</p>
          </div>
          <div className="bg-[#1a0b2e]/40 rounded-xl p-3 sm:p-4 border border-[#d4af37]/10 text-center">
            <p className="text-[#c8b6ff] text-xs mb-1">{t('Issue Date', '颁发日期')}</p>
            <p className="text-white font-semibold text-xs sm:text-sm">{cert.issueDate}</p>
          </div>
          <div className="bg-[#1a0b2e]/40 rounded-xl p-3 sm:p-4 border border-[#d4af37]/10 text-center">
            <p className="text-[#c8b6ff] text-xs mb-1">{t('Valid Until', '有效期至')}</p>
            <p className="text-white font-semibold text-xs sm:text-sm">{cert.expiryDate}</p>
          </div>
          <div className="bg-[#1a0b2e]/40 rounded-xl p-3 sm:p-4 border border-[#d4af37]/10 text-center">
            <p className="text-[#c8b6ff] text-xs mb-1">{t('Grade', '等级')}</p>
            <p className={`font-bold text-xs sm:text-sm ${gradeColor}`}>{cert.grade}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#d4af37]/20 pt-5 sm:pt-6">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-user-star-line text-[#d4af37] text-xl"></i>
            </div>
            <div>
              <p className="text-[#c8b6ff] text-xs">{t('Certified by Instructor', '导师认证')}</p>
              <p className="text-white font-semibold text-sm">SSD Mystic Academy</p>
            </div>
          </div>
          <div className="flex items-center space-x-2 text-emerald-400">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-shield-check-fill text-lg"></i>
            </div>
            <span className="text-sm font-medium">{t('Verified Authentic', '已验证真实')}</span>
          </div>
        </div>
      </div>
      <div className="h-1 w-full bg-gradient-to-r from-transparent via-[#d4af37]/50 to-transparent" />
    </div>
  );
}

export default function CertificatePage() {
  const [query, setQuery] = useState('');
  const [searched, setSearched] = useState(false);
  const [result, setResult] = useState<Certificate | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t } = useLang();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const idParam = searchParams.get('id');
    if (idParam) {
      setQuery(idParam);
      setSearched(true);
      const found = certificates.find((c) => c.id.toLowerCase() === idParam.toLowerCase());
      if (found) { setResult(found); setNotFound(false); }
      else { setResult(null); setNotFound(true); }
    }
  }, [searchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;
    setSearched(true);
    const found = certificates.find((c) => c.id.toLowerCase() === trimmed.toLowerCase());
    if (found) { setResult(found); setNotFound(false); }
    else { setResult(null); setNotFound(true); }
  };

  const handleClear = () => { setQuery(''); setSearched(false); setResult(null); setNotFound(false); };

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-[#1a0b2e] via-[#2d1b4e] to-[#1a0b2e] overflow-hidden">
      <StarField />
      <Navbar scrolled={scrolled} />

      <div className="relative z-10 pt-32 pb-24 px-4 sm:px-8">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center space-x-2 text-[#d4af37] hover:text-[#f4d03f] transition-colors duration-300 mb-8 sm:mb-10 cursor-pointer">
            <i className="ri-arrow-left-line text-xl"></i>
            <span className="whitespace-nowrap">{t('Back to Home', '返回首页')}</span>
          </Link>

          <div className="text-center mb-10 sm:mb-14">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-[#d4af37]/20 rounded-full border border-[#d4af37]/30 mb-5 sm:mb-6">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-award-line text-[#d4af37]"></i>
              </div>
              <span className="text-[#d4af37] text-sm font-medium">{t('Certificate Verification', '证书验证')}</span>
            </div>
            <h1 className="text-2xl sm:text-5xl font-serif font-bold text-white mb-4">{t('Verify Your Certificate', '验证您的证书')}</h1>
            <p className="text-[#c8b6ff] text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
              {t(
                'Enter your Certificate ID to instantly verify authenticity and view your full credential details.',
                '输入您的证书编号，即可立即验证真实性并查看完整证书详情。'
              )}
            </p>
          </div>

          <form onSubmit={handleSearch} className="mb-10 sm:mb-12">
            <div className="flex items-center bg-[#2d1b4e]/80 backdrop-blur-md border border-[#d4af37]/30 rounded-2xl overflow-hidden shadow-xl shadow-[#d4af37]/10 focus-within:border-[#d4af37]/60 transition-all duration-300">
              <div className="w-12 sm:w-14 h-12 sm:h-14 flex items-center justify-center flex-shrink-0 pl-3 sm:pl-4">
                <i className="ri-search-line text-[#d4af37] text-xl sm:text-2xl"></i>
              </div>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('Enter Certificate ID  e.g. 462505', '输入证书编号，例如 462505')}
                className="flex-1 px-2 sm:px-4 py-4 sm:py-5 bg-transparent text-white placeholder-[#c8b6ff]/50 focus:outline-none text-sm sm:text-base font-mono"
              />
              {query && (
                <button type="button" onClick={handleClear} className="w-8 sm:w-10 h-8 sm:h-10 flex items-center justify-center text-[#c8b6ff] hover:text-white transition-colors cursor-pointer mr-1 sm:mr-2">
                  <i className="ri-close-line text-xl"></i>
                </button>
              )}
              <button type="submit" className="m-1.5 sm:m-2 px-4 sm:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-[#d4af37] to-[#f4d03f] text-white font-bold rounded-xl hover:shadow-lg hover:shadow-[#d4af37]/40 transition-all duration-300 cursor-pointer whitespace-nowrap text-sm sm:text-base">
                {t('Verify', '查询')}
              </button>
            </div>
          </form>

          {searched && result && <CertificateCard cert={result} />}

          {searched && notFound && (
            <div className="text-center py-14 sm:py-20 bg-[#2d1b4e]/60 backdrop-blur-md rounded-3xl border border-red-500/20">
              <div className="w-16 sm:w-20 h-16 sm:h-20 flex items-center justify-center mx-auto mb-5 sm:mb-6">
                <i className="ri-error-warning-line text-red-400 text-5xl sm:text-6xl"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">{t('Certificate Not Found', '未找到证书')}</h3>
              <p className="text-[#c8b6ff] mb-6 max-w-sm mx-auto px-4">
                {t('No certificate matches the ID', '没有与编号匹配的证书')} <span className="text-[#d4af37] font-mono font-bold">"{query.trim()}"</span>{t('. Please double-check and try again.', '，请核对后重试。')}
              </p>
              <button onClick={handleClear} className="px-6 py-3 border border-[#d4af37]/40 text-[#d4af37] rounded-full hover:bg-[#d4af37]/10 transition-all duration-300 cursor-pointer whitespace-nowrap">
                {t('Try Again', '重新查询')}
              </button>
            </div>
          )}

          {!searched && (
            <div className="mt-4 text-center">
              <p className="text-[#c8b6ff]/60 text-sm mb-3">{t('Click to fill in certificate ID:', '点击快速填入证书编号：')}</p>
              <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
                {certificates.map((c) => (
                  <button key={c.id} onClick={() => setQuery(c.id)}
                    className="px-3 sm:px-4 py-2 bg-[#2d1b4e]/60 border border-[#d4af37]/20 rounded-full text-[#d4af37] text-xs sm:text-sm font-mono hover:border-[#d4af37]/50 hover:bg-[#d4af37]/10 transition-all duration-300 cursor-pointer whitespace-nowrap">
                    {c.id} · {c.studentName}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 mt-16 sm:mt-20">
            {[
              { icon: 'ri-shield-check-line', titleEn: 'Tamper-Proof', titleZh: '防篡改', descEn: 'Every certificate is cryptographically secured and cannot be forged or altered.', descZh: '每张证书均经过加密保护，无法伪造或篡改。' },
              { icon: 'ri-global-line', titleEn: 'Certificate Verification', titleZh: '证书认证', descEn: 'Professional certificate verification, easy and convenient to look up.', descZh: '专业证书认证，方便查询。' },
              { icon: 'ri-time-line', titleEn: 'Lifetime Valid', titleZh: '终身有效', descEn: 'Your certificate never expires — a permanent record of your achievement.', descZh: '您的证书永不过期，是您成就的永久记录。' },
            ].map((item) => (
              <div key={item.titleEn} className="bg-[#2d1b4e]/60 backdrop-blur-md rounded-2xl p-5 sm:p-6 border border-[#d4af37]/15 text-center hover:border-[#d4af37]/35 transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <i className={`${item.icon} text-[#d4af37] text-4xl`}></i>
                </div>
                <h4 className="text-white font-bold mb-2">{t(item.titleEn, item.titleZh)}</h4>
                <p className="text-[#c8b6ff] text-sm leading-relaxed">{t(item.descEn, item.descZh)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
